import { useEffect } from 'react';
import { onConsentChange, useConsent } from '../consent/consent-context';
import type { ConsentState } from '../consent/types';

/**
 * GoogleAnalytics — GA4 loader gated on Consent Mode v2.
 *
 * Assumes index.html has already declared the default-denied consent gate
 * + loaded gtag.js synchronously (see website/index.html). This component
 * only handles consent-update messages: when the user grants/revokes
 * analytics or marketing, push the corresponding gtag('consent', 'update')
 * call so GA starts/stops collecting data live without a page refresh.
 *
 * Also fires a single gtag('config', measurementId) call once on mount so
 * GA records the initial page view (with whatever consent state currently
 * applies — denied means no analytics event is sent, granted means it is).
 */

interface GoogleAnalyticsProps {
  /** One GA4 id, or every property the site reports to (one config call per id). */
  measurementId: string | readonly string[];
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

function pushConsentUpdate(state: ConsentState | null) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const analyticsStorage = state?.analytics ? 'granted' : 'denied';
  const adStorage = state?.marketing ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    analytics_storage: analyticsStorage,
    ad_storage: adStorage,
    ad_user_data: adStorage,
    ad_personalization: adStorage,
  });
}

export function GoogleAnalytics({ measurementId }: GoogleAnalyticsProps) {
  // One gtag.js load serves every property — normalise to a list and config each.
  const ids = Array.isArray(measurementId) ? measurementId : [measurementId];
  const { state } = useConsent();

  // Push current consent state on mount + fire initial config call.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    pushConsentUpdate(state);
    for (const id of ids) window.gtag('config', id, { anonymize_ip: true });
    // Run once — subsequent consent changes are handled by the listener below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(',')]);

  // Live updates when consent changes.
  useEffect(() => {
    const unsub = onConsentChange((next) => pushConsentUpdate(next));
    return unsub;
  }, []);

  return null;
}
